// =============================================================================
// Stacking Resolver
//
// Given a list of active ModifierEffects targeting the same attribute, computes
// the effective total bonus following typed-bonus stacking rules (D&D 3.5 /
// Pathfinder 1e / D&D 5e).
//
// Rules:
//   • 'stacks' types (Untyped, Dodge, Circumstance): sum every value.
//   • 'no_stack_highest' types: within each type, only the highest single
//     value applies (penalties are never suppressed — a −4 penalty always
//     applies even when there are larger penalties of the same type).
//
// Usage:
//   const effective = resolveStackedBonus(activeEffects, bonusTypeMap);
// =============================================================================
import type { AttackKind, ModifierEffect } from '../../../backend/shared/types/index';
import type { BonusTypeDefinition } from '../../../backend/shared/config/bonusTypes';

/**
 * Return the modifier attributes that apply to one attack or damage roll.
 *
 * Routines saved before attack categories were introduced have no attackKind.
 * Those routines historically applied every typed combat modifier, so retain
 * that behavior until the player explicitly classifies each attack. New and
 * edited routines use the selected category and only receive matching effects.
 */
export function combatModifierTargets(
  base: 'attack' | 'damage',
  attackKind?: AttackKind,
): Set<string> {
  if (attackKind) return new Set([base, `${base}.${attackKind}`]);
  return new Set([base, `${base}.melee`, `${base}.ranged`, `${base}.spell`]);
}

/**
 * Compute the effective total bonus for a list of effects that all target
 * the same attribute.
 *
 * @param effects       Active ModifierEffect records for one attribute.
 * @param bonusTypeMap  Map of typeId → BonusTypeDefinition (built-ins + custom).
 * @returns             Signed integer — the effective combined bonus/penalty.
 */
export function resolveStackedBonus(
  effects: ModifierEffect[],
  bonusTypeMap: Record<string, Pick<BonusTypeDefinition, 'stackingRule'>>,
): number {
  // Separate positive bonuses and negative penalties.
  // Penalties of a typed kind always stack (a −4 and a −2 both apply).
  // Bonuses follow their type's stacking rule.
  const bonusesByType  = new Map<string, number[]>();
  const penalties: number[] = [];

  for (const effect of effects) {
    // Skip roll modifier effects (advantage/disadvantage) — they have no numeric value
    if (effect.rollModifier || effect.value === undefined || !effect.bonusTypeId) continue;
    if (effect.value < 0) {
      penalties.push(effect.value);
      continue;
    }
    const list = bonusesByType.get(effect.bonusTypeId) ?? [];
    list.push(effect.value);
    bonusesByType.set(effect.bonusTypeId, list);
  }

  let total = 0;

  // Sum all penalties (penalties of the same type always stack in 3.5/PF1).
  for (const p of penalties) total += p;

  // Apply bonuses per type.
  for (const [typeId, values] of bonusesByType) {
    const rule = bonusTypeMap[typeId]?.stackingRule ?? 'stacks';
    if (rule === 'stacks') {
      total += values.reduce((s, v) => s + v, 0);
    } else {
      // no_stack_highest — take the single best bonus of this type.
      total += Math.max(...values);
    }
  }

  return total;
}

/**
 * Return the individual effects that actually contribute to a stacked total.
 * This is useful for calculation breakdowns: suppressed, lower positive bonuses
 * of a non-stacking type are intentionally omitted.
 */
export function appliedStackedEffects<T extends ModifierEffect>(
  effects: T[],
  bonusTypeMap: Record<string, Pick<BonusTypeDefinition, 'stackingRule'>>,
): T[] {
  const selected = new Set<T>();
  const bonusesByType = new Map<string, T[]>();

  for (const effect of effects) {
    if (effect.rollModifier || effect.value === undefined || !effect.bonusTypeId) continue;
    if (effect.value < 0) {
      selected.add(effect);
      continue;
    }
    const list = bonusesByType.get(effect.bonusTypeId) ?? [];
    list.push(effect);
    bonusesByType.set(effect.bonusTypeId, list);
  }

  for (const [typeId, bonuses] of bonusesByType) {
    const rule = bonusTypeMap[typeId]?.stackingRule ?? 'stacks';
    if (rule === 'stacks') {
      bonuses.forEach((effect) => selected.add(effect));
    } else if (bonuses.length > 0) {
      selected.add(bonuses.reduce((best, effect) =>
        (effect.value ?? 0) > (best.value ?? 0) ? effect : best));
    }
  }

  return effects.filter((effect) => selected.has(effect));
}

/**
 * For a character, determine which modifiers are currently active.
 *
 * @param modifiers      Full list from GET /campaigns/{id}/combat/modifiers.
 * @param characterId    The character whose view we are computing.
 */
export function activeModifiersForCharacter<
  T extends { modifierId: string; isActive: boolean; defaultEnabled: boolean; characterStates: Record<string, boolean> }
>(modifiers: T[], characterId: string): T[] {
  return modifiers.filter((m) => {
    if (!m.isActive) return false;
    const override = m.characterStates[characterId];
    return override !== undefined ? override : m.defaultEnabled;
  });
}
